import { decodePcm16Chunk } from "./audioProcessingHelpers"

// Stream PCM audio (received from Gemini Live Api) through the speakers using scheduled AudioBufferSourceNodes.
// Each PCM16 chunk is scheduled to start exactly when the previous chunk ends, producing continuous playback.
export class PcmPlayer {
  private scheduledTime = 0
  private destroyed = false

  private constructor(private playbackContext: AudioContext) {}

  static async create(): Promise<PcmPlayer> {
    const playbackContext = new AudioContext({ sampleRate: 24000 })
    await playbackContext.resume()
    return new PcmPlayer(playbackContext)
  }

  enqueueAudio(base64: string): void {
    if (this.destroyed) return

    const audioChunk = decodePcm16Chunk(base64)
    if (!audioChunk) return

    const audioBuffer = this.playbackContext.createBuffer(
      1, // number of channels
      audioChunk.length, // number of frames needed for buffer
      24000 // 24 kHz
    )
    audioBuffer.getChannelData(0).set(audioChunk)

    const source = this.playbackContext.createBufferSource()
    source.buffer = audioBuffer
    source.connect(this.playbackContext.destination)

    // Schedule this chunk to start immediately after the previous one.
    // If scheduledTime has fallen behind currentTime (first chunk or after a gap),
    // start 50ms ahead to give subsequent chunks time to arrive.
    const startTime = Math.max(
      this.scheduledTime,
      this.playbackContext.currentTime + 0.05
    )
    source.start(startTime)
    this.scheduledTime = startTime + audioBuffer.duration
  }

  close(): void {
    this.destroyed = true
    if (this.playbackContext.state !== "closed") this.playbackContext.close()
  }
}
